/**
 * The guide as people see it on the AI guide screen: split into its sections, each marked with where it comes
 * from (see compose.ts): the rules built into Genesis, the guide text super admins edit, or what Genesis
 * generates for the reader. AI assistants read the plain text (/llms.txt); this is only for showing it.
 */

/** The heading of the rules that come with Genesis (FIXED_RULES in compose.ts). */
export const BUILT_IN_HEADING = 'Rules that always apply';

/** The headings of the parts compose.ts generates after the guide text (checked by a test against it). */
export const GENERATED_HEADINGS = ['Screens', 'What you can do', 'Categories you can create pages in', 'Templates', 'Section code rules'];

/** about: the lines before the first section (who is reading, which version). */
export type GuidePart = 'about' | 'built-in' | 'guide' | 'generated';

export type GuideSection = { heading: string; part: GuidePart; text: string };

const partOf = (heading: string): GuidePart => {
  if (heading === BUILT_IN_HEADING) return 'built-in';
  if (GENERATED_HEADINGS.indexOf(heading) !== -1) return 'generated';
  return 'guide';
};

/**
 * Sections start at "## " lines outside code blocks (the section code rules hold a brief with headings of its own
 * inside a fence). The "# " title line is left out: the screen has its own.
 */
export const splitGuide = (text: string): GuideSection[] => {
  const sections: GuideSection[] = [];
  let current: GuideSection = { heading: 'About this guide', part: 'about', text: '' };
  const lines: string[] = [];
  let fence: string | null = null;
  const flush = () => {
    current.text = lines.join('\n').trim();
    if (current.part !== 'about' || current.text) sections.push(current);
    lines.length = 0;
  };
  String(text || '').replace(/\r\n?/g, '\n').split('\n').forEach((line) => {
    const opener = line.match(/^(`{3,}|~{3,})/);
    if (fence) {
      if (opener && line.trim() === opener[1] && opener[1][0] === fence[0] && opener[1].length >= fence.length) fence = null;
      lines.push(line);
      return;
    }
    if (opener) {
      fence = opener[1];
      lines.push(line);
      return;
    }
    const heading = line.match(/^## (.+?)\s*$/);
    if (heading) {
      flush();
      current = { heading: heading[1], part: partOf(heading[1]), text: '' };
      return;
    }
    if (current.part === 'about' && /^# /.test(line)) return;
    lines.push(line);
  });
  flush();
  return sections;
};
